import { useEffect, useState } from "react";
import markdownStyles from "../../components/markdown-styles.module.css";

export default function PostBody({ content }) {
    const [headings, setHeadings] = useState([]);
    const [expanded, setExpanded] = useState(false);

    useEffect(() => {
        if (!content) return;

        // Parse h2 and h3 in document order to preserve hierarchy
        const sections = [];
        const regex = /<(h[23]) id=\"([^\"]+)\">([\s\S]*?)<\/h[23]>/g;
        let match;

        const decode = (html) =>
            html
                .replace(/<[^>]+>/g, "")
                .replace(/&nbsp;|&#160;/gi, " ")
                .replace(/&amp;/g, "&")
                .replace(/&lt;/g, "<")
                .replace(/&gt;/g, ">")
                .replace(/&quot;/g, '"')
                .replace(/&#39;/g, "'")
                .trim();

        while ((match = regex.exec(content)) !== null) {
            const level = match[1];
            const id = match[2];
            const text = decode(match[3]);

            if (level === "h2") {
                sections.push({ id, text, children: [] });
            } else if (level === "h3") {
                if (sections.length === 0) {
                    // no h2 yet, create a placeholder parent
                    sections.push({ id: `section-${id}`, text: "More", children: [{ id, text }] });
                } else {
                    sections[sections.length - 1].children.push({ id, text });
                }
            }
        }

        // Flatten to an array-like structure for compatibility with previous rendering if needed
        // but keep hierarchy in `sections`
        setHeadings(sections);
    }, [content]);

    const scrollToId = (id) => {
        const target = document.getElementById(id);
        if (!target) return;
        const yOffset = -100;
        const y = target.getBoundingClientRect().top + window.pageYOffset + yOffset;
        window.scrollTo({ top: y, behavior: "smooth" });
    };

    const visibleSections = expanded ? headings : headings.slice(0, 5);
    const hasChildren = headings.some((s) => s.children && s.children.length > 0);

    return (
        <div className="w-full px-4 sm:px-6 md:px-8 lg:px-10 bg-gray-50">
            <div className="mx-auto w-full    px-5 py-7  sm:px-8 sm:py-10 lg:px-10 lg:py-12">

                {/* TOC box above content */}
                {headings.length > 0 && (
                    <div className="w-full mb-6">
                        <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-md max-w-full">
                            <div className="flex items-start justify-between mb-3">
                                <div className="flex items-center gap-3">
                                    <div className="p-2 bg-[#fff4ef] rounded-md">
                                        <svg width="18" height="14" viewBox="0 0 18 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <rect x="0" y="1" width="18" height="2" rx="1" fill="#ff9245" />
                                            <rect x="0" y="6" width="18" height="2" rx="1" fill="#ff9245" />
                                            <rect x="0" y="11" width="18" height="2" rx="1" fill="#ff9245" />
                                        </svg>
                                    </div>
                                    <h3 className="text-sm font-bold text-[#ff9245] uppercase">Table of Contents</h3>
                                </div>
                            </div>

                            <ul className="space-y-2">
                                {visibleSections.map((section, i) => (
                                    <li key={section.id} className="">
                                        <div className="flex items-start gap-3">
                                            <div className="text-sm text-gray-500 font-bold w-6 text-right">{headings.indexOf(section) + 1}</div>
                                            <button onClick={() => scrollToId(section.id)} className="text-sm text-left text-[#222] hover:text-[#FF914D]">
                                                {section.text}
                                            </button>
                                        </div>

                                        {expanded && section.children && section.children.length > 0 && (
                                            <ul className="mt-2 ml-8 space-y-2">
                                                {section.children.map((child, j) => (
                                                    <li key={child.id} className="flex items-start gap-3">
                                                        <div className="text-sm text-gray-500 font-bold w-10 text-right">{`${headings.indexOf(section) + 1}.${j + 1}`}</div>
                                                        <button onClick={() => scrollToId(child.id)} className="text-sm text-left text-[#222] hover:text-[#FF914D]">
                                                            {child.text}
                                                        </button>
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                    </li>
                                ))}
                            </ul>

                            <div className="text-center mt-4">
                                {(hasChildren || headings.length > 5) && (
                                    <button onClick={() => setExpanded((s) => !s)} className="text-sm font-semibold text-[#FF914D]">
                                        {expanded ? "See less ↑" : "See more sections ↓"}
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                <div
                    className={`${markdownStyles.markdown} max-w-none text-[16px] leading-8 text-slate-700 [&_a]:font-semibold [&_a]:text-[#FF914D] [&_a]:underline-offset-4 hover:[&_a]:underline [&_h1]:text-3xl [&_h1]:font-black [&_h1]:text-slate-950 [&_h1]:mt-8 [&_h1]:mb-5 [&_h2]:text-[22px] [&_h2]:font-black [&_h2]:text-[#1a2b5f] [&_h2]:leading-tight [&_h2]:mt-8 [&_h2]:mb-4 [&_h2]:border-b [&_h2]:border-slate-200 [&_h2]:pb-2 [&_h3]:text-lg [&_h3]:font-extrabold [&_h3]:text-slate-900 [&_h3]:mt-7 [&_h3]:mb-3 [&_h4]:text-base [&_h4]:font-bold [&_h4]:text-slate-900 [&_p]:mb-5 [&_ul]:mb-6 [&_ul]:pl-6 [&_ul]:space-y-2 [&_ol]:mb-6 [&_ol]:pl-6 [&_ol]:space-y-2 [&_li]:leading-7 [&_img]:my-8 [&_img]:w-full [&_img]:rounded-3xl [&_img]:shadow-lg [&_blockquote]:my-8 [&_blockquote]:rounded-2xl [&_blockquote]:border-l-4 [&_blockquote]:border-[#FF914D] [&_blockquote]:bg-[#fff8f2] [&_blockquote]:px-6 [&_blockquote]:py-5 [&_table]:my-8 [&_table]:w-full [&_table]:border-collapse [&_table]:text-sm [&_th]:border [&_th]:border-slate-200 [&_th]:bg-[#fff1e5] [&_th]:px-4 [&_th]:py-3 [&_th]:text-left [&_th]:font-extrabold [&_td]:border [&_td]:border-slate-200 [&_td]:px-4 [&_td]:py-3`}
                    dangerouslySetInnerHTML={{ __html: content }}
                />
            </div>
        </div>
    );
}